import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UrlForm from "@/components/UrlForm";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo-data";
import {
  Globe,
  Lock,
  FileText,
  Phone,
  Search,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const SIGNALS = [
  {
    icon: Lock,
    cat: "Sécurité",
    key: "Protocole HTTPS",
    desc: "Vérification que le site utilise une connexion chiffrée (HTTPS). Sans HTTPS, les données échangées ne sont pas sécurisées.",
    severity: "high",
  },
  {
    icon: Globe,
    cat: "Domaine",
    key: "Structure du domaine",
    desc: "Analyse du nom de domaine pour détecter des extensions peu courantes, des tirets suspects ou des séquences de chiffres inhabituelles.",
    severity: "medium",
  },
  {
    icon: Globe,
    cat: "Domaine",
    key: "Âge du domaine",
    desc: "Vérification de la date de création du domaine via WHOIS. Un domaine très récent peut mériter une vigilance renforcée, surtout lorsqu'il est associé à des promotions agressives ou à peu d'informations vérifiables.",
    severity: "medium",
  },
  {
    icon: FileText,
    cat: "Informations légales",
    key: "Mentions légales",
    desc: "En France, tout site commercial doit publier des mentions légales. Leur absence est un signal fort d'alerte, surtout pour une boutique qui vend à des particuliers.",
    severity: "high",
  },
  {
    icon: FileText,
    cat: "Informations légales",
    key: "Conditions générales de vente (CGV)",
    desc: "Les CGV sont obligatoires pour tout site de vente en ligne français. Elles précisent vos droits en tant qu'acheteur : livraison, retours, remboursement, garanties et responsabilités du vendeur.",
    severity: "high",
  },
  {
    icon: FileText,
    cat: "Informations légales",
    key: "Politique de confidentialité",
    desc: "Vérification de la présence d'une politique de confidentialité, utile pour comprendre comment le site collecte, utilise et protège les données personnelles.",
    severity: "medium",
  },
  {
    icon: Phone,
    cat: "Contact",
    key: "Page de contact",
    desc: "Présence d'une page permettant de contacter le site. Sans moyen de contact clair, tout recours après achat peut devenir plus difficile.",
    severity: "medium",
  },
  {
    icon: Phone,
    cat: "Contact",
    key: "Coordonnées (email / téléphone)",
    desc: "Présence d'un email, d'un numéro de téléphone ou d'un moyen de contact identifiable. Un site sérieux doit proposer un canal de contact compréhensible.",
    severity: "medium",
  },
  {
    icon: Search,
    cat: "Identité commerciale",
    key: "Informations société",
    desc: "Recherche d'informations sur l'entreprise exploitant le site : SIRET, RCS, raison sociale, adresse, statut juridique ou équivalents internationaux.",
    severity: "medium",
  },
  {
    icon: AlertTriangle,
    cat: "Indices marketing",
    key: "Promotions suspectes",
    desc: "Détection de remises très importantes, d'urgences commerciales artificielles, de faux compteurs ou de formulations commerciales excessivement pressantes.",
    severity: "medium",
  },
  {
    icon: AlertTriangle,
    cat: "Contenu",
    key: "Formulations suspectes",
    desc: "Détection de tournures créant une urgence artificielle : « dernière chance », « stock limité », « offre expire dans... » ou autres messages conçus pour accélérer l'achat.",
    severity: "medium",
  },
  {
    icon: Search,
    cat: "Contenu",
    key: "Qualité du contenu",
    desc: "Un contenu très pauvre, très générique, incohérent ou manifestement traduit automatiquement peut indiquer un site créé rapidement et sans réel soin éditorial.",
    severity: "low",
  },
  {
    icon: AlertTriangle,
    cat: "Sécurité",
    key: "Accessibilité du site",
    desc: "Si le site n'est pas accessible lors de l'analyse, ou s'il bloque fortement l'inspection automatisée, certaines vérifications peuvent devenir impossibles ou partielles.",
    severity: "high",
  },
];

const SEVERITY_LABELS = {
  high: "Élevée",
  medium: "Moyenne",
  low: "Faible",
};

const SEVERITY_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-orange-100 text-orange-700 border-orange-200",
  low: "bg-yellow-100 text-yellow-700 border-yellow-200",
};

const SCORE_LEVELS = [
  {
    range: "0 à 39",
    label: "Risque élevé",
    desc: "Plusieurs signaux de vigilance ont été détectés. Évitez d'acheter sans vérification approfondie.",
    color: "bg-red-50 border-red-200 text-red-800",
  },
  {
    range: "40 à 69",
    label: "Vigilance recommandée",
    desc: "Certains signaux méritent une vérification manuelle avant tout achat.",
    color: "bg-orange-50 border-orange-200 text-orange-800",
  },
  {
    range: "70 à 100",
    label: "Risque faible",
    desc: "Peu de signaux problématiques ont été détectés. Cela ne garantit pas l'absence totale de risque.",
    color: "bg-green-50 border-green-200 text-green-800",
  },
];

export default function SignalsPage() {
  const categories = [...new Set(SIGNALS.map((s) => s.cat))];

  const firstGroupCategories = ["Sécurité", "Domaine", "Informations légales"];
  const secondGroupCategories = [
    "Contact",
    "Identité commerciale",
    "Indices marketing",
    "Contenu",
  ];

  const renderSignalGroup = (groupCategories) =>
    categories
      .filter((cat) => groupCategories.includes(cat))
      .map((cat) => (
        <div key={cat} className="mb-10">
          <h2 className="text-lg font-bold font-heading text-[#1E3A8A] mb-4 border-b border-slate-200 pb-2">
            {cat}
          </h2>

          <div className="space-y-3">
            {SIGNALS.filter((s) => s.cat === cat).map((signal) => {
              const Icon = signal.icon;

              return (
                <div
                  key={signal.key}
                  className="card-base p-5 flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-800 text-sm">
                        {signal.key}
                      </span>

                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                          SEVERITY_COLORS[signal.severity]
                        }`}
                      >
                        Importance : {SEVERITY_LABELS[signal.severity]}
                      </span>
                    </div>

                    <p className="text-sm text-slate-600 leading-relaxed">
                      {signal.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ));

  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Les signaux analysés — Transparence du scoring"
        description="Liste détaillée des 12+ signaux que Fiable ou Pas analyse pour vérifier un site marchand : HTTPS, mentions légales, CGV, contact, contenu, identité commerciale."
        path="/signaux"
        keywords="signaux fiabilité site, mentions légales CGV, HTTPS, scoring e-commerce"
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Signaux analysés", path: "/signaux" },
        ])}
      />

      <Header />

      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-4xl">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">
              Transparence
            </span>

            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 tracking-tight mt-3 mb-4">
              Les signaux analysés
            </h1>

            <p className="text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
              Fiable ou Pas analyse automatiquement plus de 12 signaux pour
              calculer un score de confiance d&apos;un site. Voici exactement ce
              que nous vérifions et pourquoi.
            </p>
          </div>

          {renderSignalGroup(firstGroupCategories)}

          <div className="bg-white border border-blue-100 rounded-2xl p-6 mb-10 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1E3A8A] mb-2">
              Lecture des signaux
            </p>

            <p className="text-sm text-slate-600 leading-relaxed">
              Un signal n&apos;est pas une preuve définitive. C&apos;est un
              indicateur à interpréter avec les autres éléments de l&apos;analyse.
              Par exemple, un domaine récent n&apos;est pas forcément frauduleux,
              mais un domaine récent associé à des prix irréalistes, peu
              d&apos;informations légales et aucun contact identifiable mérite une
              vigilance renforcée.
            </p>
          </div>

          {renderSignalGroup(secondGroupCategories)}

          <div className="card-base p-8 mb-10">
            <h2 className="text-xl font-bold font-heading text-slate-800 mb-4">
              Comment le score est calculé
            </h2>

            <p className="text-sm text-slate-600 mb-4 leading-relaxed">
              Chaque signal de vigilance confirmé retire des points au score de
              confiance, selon son importance. Le score final est exprimé sur
              100 : plus il est élevé, plus les signaux détectés sont rassurants.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {SCORE_LEVELS.map(({ range, label, desc, color }) => (
                <div key={range} className={`rounded-xl border p-4 ${color}`}>
                  <div className="font-bold text-lg mb-1">{range}</div>
                  <div className="font-semibold text-sm mb-2">{label}</div>
                  <div className="text-xs opacity-80">{desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-sm text-slate-600 mb-10">
            <p className="font-semibold text-slate-800 mb-2">Avertissement</p>

            <p>
              Ces signaux sont détectés automatiquement. Ils peuvent être
              incomplets, indisponibles ou imprécis selon les sites. Ils ne
              constituent pas un jugement définitif sur la fiabilité d&apos;un
              site. Un site peut présenter certains signaux de vigilance tout en
              étant légitime. À l&apos;inverse, un score élevé ne garantit jamais
              une absence totale de risque.
            </p>
          </div>

          <div className="text-center">
            <h3 className="font-bold text-slate-800 mb-4">
              Tester ces signaux sur un site ?
            </h3>

            <UrlForm />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}